import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { applicationTypePath, ROUTES } from "@/app/routes";
import { Skeleton } from "@/components/ui/skeleton";
import {
  groupModelsByPath,
  searchModels,
} from "@/domain/process-model/group-models";

import { type RunnableModel, useRunnableModels } from "./use-runnable-models";

/**
 * 起票の入口（マイ案件の「新規作成」から遷移）。
 *
 * 案件種別＝プロセスモデル。**選んでも案件は作らず**、種別の詳細
 * （/my-applications/new/:modifiedModelId）へ送る。以前は選んだ瞬間に instance を
 * 作っていたため、流れを確認するだけで下書きが増えていた。
 *
 * 実際の作成は詳細画面で行う（起票の経路の違いもそちらで吸収する）。
 */
export function NewApplicationPage() {
  const { t } = useTranslation(["common", "domain"]);
  const navigate = useNavigate();
  const { data, isPending, isError } = useRunnableModels();
  const [keyword, setKeyword] = useState("");

  // 階層はモデル id のパスから導く（グループの表示名は API の parent_groups 由来）
  const groups = groupModelsByPath(searchModels(data ?? [], keyword));

  const selectModel = (model: RunnableModel) => {
    void navigate(applicationTypePath(model.processModelId));
  };

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">
          {t("common:newApplication.title")}
        </h1>
        <p className="text-muted-foreground text-sm">
          {t("common:newApplication.description")}
        </p>
      </header>

      {isPending ? <CatalogSkeleton /> : null}

      {isError ? (
        <p className="text-destructive text-sm">{t("common:state.error")}</p>
      ) : null}

      {data ? (
        data.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("common:newApplication.noRunnable")}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <SearchBox
              label={t("common:newApplication.searchTypes")}
              value={keyword}
              onChange={setKeyword}
            />

            {groups.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("common:newApplication.noMatch")}
              </p>
            ) : null}

            {groups.map((group) => (
              <section key={group.path} className="flex flex-col gap-2">
                {group.label === "" ? null : (
                  <h2 className="text-muted-foreground text-xs font-semibold">
                    {group.label}
                  </h2>
                )}
                <ul className="grid gap-3 sm:grid-cols-2">
                  {group.models.map((model) => (
                    <li key={model.processModelId}>
                      <CatalogCard
                        model={model}
                        onSelect={() => selectModel(model)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )
      ) : null}

      <Link
        to={ROUTES.myApplications}
        className="text-primary text-sm hover:underline"
      >
        {t("common:newApplication.back")}
      </Link>
    </section>
  );
}

function CatalogCard({
  model,
  onSelect,
}: {
  readonly model: RunnableModel;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="border-border hover:border-primary bg-card flex h-full w-full flex-col gap-1 rounded-lg border p-4 text-left disabled:opacity-60"
    >
      <span className="font-medium">{model.displayName}</span>
      {model.description === null ? null : (
        <span className="text-muted-foreground text-xs">
          {model.description}
        </span>
      )}
    </button>
  );
}

function CatalogSkeleton() {
  return (
    <div className="grid gap-3 sm:grid-cols-2" aria-hidden="true">
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-20 w-full" />
    </div>
  );
}

/** カタログの絞り込み用の検索欄。 */
function SearchBox({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (_value: string) => void;
}) {
  const id = useId();

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type="search"
        value={value}
        placeholder={label}
        onChange={(event) => onChange(event.target.value)}
        className="border-input bg-background max-w-md rounded-md border px-2 py-1.5 text-sm"
      />
    </div>
  );
}
